import React, { useCallback, useState, useRef, useEffect } from 'react';

import { Icon } from './icon';
import { Button } from './shadcn/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './shadcn/tooltip';
import { TrackingEventsV2 } from '../../analytics/eventsV2';
import { trackCustomEvent } from '../../analytics/Mixpanel';

interface NaturalLanguageSearchBoxProps {
  value: string;
  processingNL?: boolean;
  entityType: 'facts' | 'documents' | 'docs-in-subset-creator';

  onChange: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  onNLComplete?: () => void;
  onNLSearchExecuted?: (trigger: 'wand' | 'spacebar') => void;
  clearSearch: () => void;
}

export const NaturalLanguageSearchBox = ({
  value,
  processingNL,
  entityType,
  onChange,
  onSearchCall,
  onNLComplete,
  onNLSearchExecuted,
  clearSearch,
}: NaturalLanguageSearchBoxProps) => {
  const [text, setText] = useState(value);
  const [isNaturalLanguageMode, setIsNaturalLanguageMode] = useState(false);
  const [wasNLQuery, setWasNLQuery] = useState(false);
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [nlActivationTrigger, setNlActivationTrigger] = useState<'wand' | 'spacebar' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onTextChange = useCallback(
    (text: string) => {
      setText(text);
      onChange?.(text);
    },
    [onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' && text === '') {
      e.preventDefault();
      setIsNaturalLanguageMode(true);
      setNlActivationTrigger('spacebar');
      trackCustomEvent(TrackingEventsV2.NaturalLanguageSearchViaSpace);
      inputRef.current?.focus();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isNaturalLanguageMode) {
        setWasNLQuery(true);
        // Track NL search execution if trigger is set
        if (nlActivationTrigger) {
          onNLSearchExecuted?.(nlActivationTrigger);
        }
      }
      onSearchCall?.(text, isNaturalLanguageMode);
    }
    if (e.key === 'Escape' && isNaturalLanguageMode) {
      setIsNaturalLanguageMode(false);
      setNlActivationTrigger(null);
      if (text === '') {
        clearSearch?.();
      }
    }
  };

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    // When NL processing completes, exit NL mode
    if (wasNLQuery && !processingNL) {
      setWasNLQuery(false);
      setNlActivationTrigger(null);
      onNLComplete?.();
    }
  }, [processingNL, wasNLQuery, onNLComplete]);

  useEffect(() => {
    if (isNaturalLanguageMode && !text && !processingNL) {
      const examples = getExamples();
      const currentExample = examples[currentExampleIndex];
      const words = currentExample.split(' ');

      const totalWordsInCurrentExample = words.length;
      const animationDuration = totalWordsInCurrentExample * 150 + 300;
      const displayTime = 2000;

      const timer = setTimeout(() => {
        setCurrentExampleIndex((prevIndex) => (prevIndex + 1) % examples.length);
      }, animationDuration + displayTime);

      return () => clearTimeout(timer);
    }
  }, [isNaturalLanguageMode, currentExampleIndex, text, processingNL, entityType]);

  const getExamples = () => {
    if (entityType === 'facts') {
      return [
        'Find disputed facts about employment issues...',
        'Get relevant facts from emails in 2023...',
        'Show facts to do with John Smith & Acme Corp...',
      ];
    } else if (entityType === 'docs-in-subset-creator') {
      return [
        'Show contracts and articles...',
        'Find emails to do with John Smith & Acme Corp...',
        'Documents about monopolisation, 1990 - 2000...',
      ];
    } else {
      return [
        'Show contracts from last month with key facts...',
        'Find emails about merger discussions...',
        'Get processed documents from 2023 marked relevant...',
      ];
    }
  };

  const renderPlaceholder = () => {
    if (text || processingNL) return null;

    if (isNaturalLanguageMode) {
      const examples = getExamples();
      const currentExample = examples[currentExampleIndex];
      const words = currentExample.split(' ');

      return (
        <div className="pointer-events-none absolute top-1/2 left-8.5 -translate-y-1/2 whitespace-pre text-sky-500">
          {words.map((word, index) => (
            <span
              key={`${currentExampleIndex}-${index}`}
              className="inline-block opacity-0"
              style={{
                animation: `fadeIn 0.3s ease-out forwards`,
                animationDelay: `${index * 150}ms`,
              }}
            >
              {word}
              {index < words.length - 1 ? ' ' : ''}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div className="pointer-events-none absolute top-1/2 left-9 line-clamp-1 -translate-y-1/2 break-all text-gray-400">
        Search {entityType} or press
        <kbd className="mx-1.5">Space</kbd>
        to apply filters using natural language
      </div>
    );
  };

  return (
    <div className="flex grow items-center gap-1.5">
      {!processingNL && !isNaturalLanguageMode && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="py-2.25"
                onClick={() => {
                  setIsNaturalLanguageMode(true);
                  setNlActivationTrigger('wand');
                  trackCustomEvent(TrackingEventsV2.NaturalLanguageSearchViaWand);

                  inputRef.current?.focus();
                }}
              >
                <Icon className="lucide--wand-sparkles" />
              </Button>
            </TooltipTrigger>

            <TooltipContent side="bottom">Describe what you're looking for using natural language</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <div
        className={`relative flex w-full flex-row items-center justify-center rounded-md border ${
          isNaturalLanguageMode ? 'border-sky-200 bg-sky-50' : 'border-gray-300 bg-white'
        } min-w-md text-sm transition-all duration-200`}
        data-testid="natural-language-search-bar"
      >
        <div className="mx-2.5">
          {processingNL ? (
            <Icon className="lucide--loader-circle animate-spin text-sky-400 duration-1500" />
          ) : isNaturalLanguageMode ? (
            <Icon className="lucide--wand-sparkles text-sky-400" />
          ) : (
            <Icon className="lucide--search text-base text-gray-400" />
          )}
        </div>

        <input
          ref={inputRef}
          className={`w-full rounded-md bg-transparent py-1.5 font-normal text-black not-italic placeholder:text-gray-500 focus:outline-none ${
            isNaturalLanguageMode ? 'text-sky-800' : 'text-gray-900'
          }`}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          value={text}
          disabled={processingNL}
        />

        {renderPlaceholder()}

        {clearSearch && text && (
          <button
            className={`mr-2 cursor-pointer ${isNaturalLanguageMode ? 'text-sky-400' : 'text-gray-400'}`}
            onClick={() => {
              clearSearch();
              setText('');
              setIsNaturalLanguageMode(false);
              setWasNLQuery(false);
              setNlActivationTrigger(null);
            }}
          >
            <Icon className="lucide--x h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
